// The map of the suite (ADR-286): the rings from the inside out, who stands on them, and the journeys that cross them.
// Coordinates are those of the map's viewBox, centred on Nexxt; angles go clockwise from the right, as in SVG.

export type AppId = "backstage" | "guestlist" | "atelier" | "frontrow";
export type RingId = "core" | "tomorrow" | "partners" | "customers" | "world";
export type NodeId = AppId | "nexxt" | "showroom" | "til" | "bc" | "ims" | "gxo" | "joor" | "zendesk" | "claude";
export type FlowId = "collection" | "invitation" | "order" | "purchase";

type NodeKind = "core" | "app" | "place" | "door" | "system";

export const apps: readonly AppId[] = ["backstage", "guestlist", "atelier", "frontrow"];

/** From the inside out: the order of the page too. */
export const rings: readonly { id: RingId; radius: number }[] = [
  { id: "core", radius: 70 },
  { id: "tomorrow", radius: 180 },
  { id: "partners", radius: 280 },
  { id: "customers", radius: 370 },
  { id: "world", radius: 455 },
];

/** Half the side of the viewBox: the outer ring and the labels outside it. */
export const mapExtent = 560;

const node = (ring: RingId, angle: number, kind: NodeKind) => ({ ring, angle, kind });

export const nodes: Readonly<Record<NodeId, { ring: RingId; angle: number; kind: NodeKind }>> = {
  nexxt: node("core", 0, "core"),
  backstage: node("tomorrow", 205, "app"),
  guestlist: node("tomorrow", 335, "app"),
  atelier: node("partners", 130, "app"),
  frontrow: node("customers", 35, "app"),
  showroom: node("customers", 62, "place"),
  claude: node("world", 160, "system"),
  joor: node("world", 198, "system"),
  bc: node("world", 232, "system"),
  til: node("world", 270, "door"),
  ims: node("world", 308, "system"),
  gxo: node("world", 342, "system"),
  zendesk: node("world", 15, "system"),
};

/** The systems Nexxt reaches only through TIL (CLAUDE.md: no direct integrations); Claude comes in through MCP. */
const behindTil: ReadonlySet<NodeId> = new Set(["bc", "ims", "gxo", "joor", "zendesk"]);

/** Every journey by its steps: one sentence each in the messages (`flow_<id>_<n>`). */
export const flows: readonly { id: FlowId; steps: readonly NodeId[] }[] = [
  { id: "collection", steps: ["atelier", "backstage", "joor", "frontrow"] },
  { id: "invitation", steps: ["guestlist", "frontrow", "showroom", "guestlist"] },
  { id: "order", steps: ["joor", "backstage", "frontrow", "gxo", "bc", "frontrow"] },
  { id: "purchase", steps: ["backstage", "atelier", "gxo", "ims"] },
];

export function position(id: NodeId): { x: number; y: number } {
  const { ring, angle } = nodes[id];
  const radius = rings.find((r) => r.id === ring)?.radius ?? 0;
  if (ring === "core") return { x: 0, y: 0 };
  const radians = (angle * Math.PI) / 180;
  return { x: round(Math.cos(radians) * radius), y: round(Math.sin(radians) * radius) };
}

/** The nodes a journey passes through: TIL stands between Nexxt and every system behind it, in both directions. */
export function route(steps: readonly NodeId[]): NodeId[] {
  return steps.flatMap((step, i) => {
    const previous = steps[i - 1];
    const crosses = previous !== undefined && (behindTil.has(previous) || behindTil.has(step));
    return crosses ? ["til" as const, step] : [step];
  });
}

/**
 * The path of a route, one curve per leg, each bent towards the centre: everything goes through Nexxt. A leg that
 * stays on one node (the same app twice) is skipped.
 */
export function pathOf(stops: readonly NodeId[]): string {
  const points = stops.map(position);
  const first = points[0];
  if (!first) return "";
  let d = `M ${first.x} ${first.y}`;
  for (let i = 1; i < points.length; i++) {
    const from = points[i - 1] as { x: number; y: number };
    const to = points[i] as { x: number; y: number };
    if (from.x === to.x && from.y === to.y) continue;
    const cx = round(((from.x + to.x) / 2) * 0.45);
    const cy = round(((from.y + to.y) / 2) * 0.45);
    d += ` Q ${cx} ${cy} ${to.x} ${to.y}`;
  }
  return d;
}

const round = (value: number) => Math.round(value * 10) / 10;
